import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import EngineeringIcon from "@mui/icons-material/Engineering";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import { adminInvite } from "../../api/auth";
import DashboardLayout from "../../layout/DashboardLayout";
import { brand } from "../../theme";
import { ROLES as ROLE_VALUES } from "../../constants/roles";

const ROLES = [
  {
    value: ROLE_VALUES.SALES_ACCOUNT_SPECIALIST,
    label: "Sales/Account Specialist",
    icon: <SupportAgentIcon />,
    color: brand.goldDark,
    bg: "#FBF3D9",
    border: brand.goldLight,
    description: "Owns an agency end-to-end for their territory — onboarding, leads, PCC requests, billing and account relationship.",
    permissions: ["View agencies assigned to their territory", "Track onboarding progress", "Request PCC on behalf", "Manage billing and contracts for their agencies"],
  },
  {
    value: ROLE_VALUES.HELPDESK,
    label: "Helpdesk",
    icon: <HeadphonesIcon />,
    color: "#e11d48",
    bg: "#fff1f2",
    border: "#fecdd3",
    description: "Handles support tickets, PCC requests, and agency queries.",
    permissions: ["Process PCC requests", "Manage support tickets", "View KYC data"],
  },
  {
    value: ROLE_VALUES.TECHNICAL_SUPPORT_SPECIALIST,
    label: "Technical Support Specialist",
    icon: <EngineeringIcon />,
    color: brand.grayDark,
    bg: brand.grayLight,
    border: brand.gray,
    description: "Technical/product support for agencies. Permissions for this role are still being finalized.",
    permissions: ["View all agencies (baseline access)"],
  },
];

export default function InviteAdmin() {
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState(ROLE_VALUES.SALES_ACCOUNT_SPECIALIST);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    setSuccess("");
    setError("");
    try {
      await adminInvite({ email, role: selectedRole });
      setSuccess(email);
      setEmail("");
      setSelectedRole(ROLE_VALUES.SALES_ACCOUNT_SPECIALIST);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send invitation.");
    } finally {
      setSubmitting(false);
    }
  };

  const activeRole = ROLES.find((r) => r.value === selectedRole);

  return (
    <DashboardLayout>
      <Box>
        {/* Page header */}
        <Box mb={4}>
          <Box display="flex" alignItems="center" gap={1.5} mb={0.75}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "10px",
                background: brand.terracotta,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                flexShrink: 0,
              }}
            >
              <PersonAddAlt1Icon sx={{ fontSize: "1.1rem" }} />
            </Box>
            <Typography variant="h5" color="#0f172a">
              Invite Sub-Admin
            </Typography>
          </Box>
          <Typography variant="body2" color="#64748b" ml={0.25}>
            Send an invitation to a new admin. Choose their role carefully — it
            determines what they can access across the platform.
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="flex-start">
          {/* Left: role picker + form */}
          <Grid item xs={12} md={7}>
            {/* Role selection */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontWeight: 600,
                    display: "block",
                    mb: 2,
                  }}
                >
                  Select Role
                </Typography>

                <Box display="flex" flexDirection="column" gap={1.5}>
                  {ROLES.map((role) => {
                    const isActive = selectedRole === role.value;
                    return (
                      <Box
                        key={role.value}
                        onClick={() => setSelectedRole(role.value)}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          p: 2,
                          borderRadius: "10px",
                          border: isActive
                            ? `2px solid ${role.color}`
                            : "1px solid #e2e8f0",
                          bgcolor: isActive ? role.bg : "#fff",
                          cursor: "pointer",
                          transition: "all 0.15s ease",
                          boxShadow: isActive
                            ? `0 0 0 3px ${role.color}18`
                            : "none",
                          "&:hover": {
                            borderColor: role.color,
                            bgcolor: role.bg,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "10px",
                            bgcolor: isActive ? role.color : "#f1f5f9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: isActive ? "#fff" : "#94a3b8",
                            "& svg": { fontSize: "1.1rem" },
                            transition: "all 0.15s ease",
                            flexShrink: 0,
                          }}
                        >
                          {role.icon}
                        </Box>
                        <Box flex={1}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.25}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              color={isActive ? "#0f172a" : "#475569"}
                            >
                              {role.label}
                            </Typography>
                            {isActive && (
                              <CheckCircleIcon
                                sx={{ fontSize: "0.95rem", color: role.color }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="#64748b">
                            {role.description}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>

            {/* Email + send */}
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontWeight: 600,
                    display: "block",
                    mb: 2,
                  }}
                >
                  Send Invitation
                </Typography>

                {success && (
                  <Alert
                    severity="success"
                    icon={<CheckCircleIcon fontSize="small" />}
                    sx={{ mb: 2, borderRadius: "8px" }}
                    onClose={() => setSuccess("")}
                  >
                    Invitation sent to <strong>{success}</strong> as{" "}
                    <strong>
                      {ROLES.find((r) => r.value === selectedRole)?.label}
                    </strong>
                    .
                  </Alert>
                )}

                {error && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2, borderRadius: "8px" }}
                    onClose={() => setError("")}
                  >
                    {error}
                  </Alert>
                )}

                <Box
                  component="form"
                  onSubmit={handleSubmit}
                  display="flex"
                  gap={1.5}
                  flexWrap="wrap"
                >
                  <TextField
                    type="email"
                    label="Admin Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    sx={{ flex: 1, minWidth: 220 }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={submitting || !email}
                    startIcon={<SendIcon />}
                    sx={{ alignSelf: "flex-end", height: 40 }}
                  >
                    {submitting ? "Sending…" : "Send Invite"}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Right: role permission summary */}
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                border: `1px solid ${activeRole.border}`,
                bgcolor: activeRole.bg,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: "10px",
                      bgcolor: activeRole.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      "& svg": { fontSize: "1.1rem" },
                      flexShrink: 0,
                    }}
                  >
                    {activeRole.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" color="#0f172a">
                      {activeRole.label}
                    </Typography>
                    <Chip
                      label="Selected role"
                      size="small"
                      sx={{
                        bgcolor: activeRole.color,
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.7rem",
                        mt: 0.25,
                      }}
                    />
                  </Box>
                </Box>

                <Typography variant="body2" color="#475569" mb={2}>
                  {activeRole.description}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Typography
                  variant="caption"
                  sx={{
                    color: "#94a3b8",
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    fontWeight: 600,
                    display: "block",
                    mb: 1.5,
                  }}
                >
                  Permissions
                </Typography>

                <Box display="flex" flexDirection="column" gap={1}>
                  {activeRole.permissions.map((perm) => (
                    <Box key={perm} display="flex" alignItems="center" gap={1}>
                      <CheckCircleIcon
                        sx={{ fontSize: "0.95rem", color: activeRole.color }}
                      />
                      <Typography variant="body2" color="#475569">
                        {perm}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}
